import { List } from "lucide-react";
import { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { transactionDetailsViewAtom } from "@/app/store";
import { useCrefazContext } from "@/app/contexts/CrefazContext";
import { Button } from "../../../../../../components/ui/button";
import { TransactionsDetailsInfo } from "./components/TransactionsDetailsInfo";
import { TransactionDetailsCard } from "./components/TransactionDetailsCard";
import { ObservationsHistory } from "./components/ObservationsHistory";
import { useTransactionDetails } from "./useTransactionDetails";
import { Spinner } from "../../../../../../components/ui/spinner";
import { ModalRefuseTransaction } from "./components/RefuseTransactionModal";
import { ModalApproveTransaction } from "./components/ApproveTransactionModal";
import { ModalUploadFiles } from "../../../Simulations/Crefaz/components/UploadFilesModal";
import { ModalSimulatePan } from "./components/SimulatePanModal";
import { useAuth } from "@/app/hooks/useAuth";
import { StatusEnum } from "@/app/utils/status";
import { useLocation } from "react-router-dom";
import {
  PROPOSAL_DETAILS_STEP,
  useRegisterSupportContext,
} from "@/app/store/supportScreenContext";
import { PartnerType } from "@/app/utils/partners.enum";

type TransactionDetailsProps = {
  isClient?: boolean;
  onDocuments?: () => void;
  onSupport?: () => void;
};

const TransactionDetails = ({
  isClient = false,
  onSupport,
  onDocuments,
}: TransactionDetailsProps) => {
  const [simulatePanOpen, setSimulatePanOpen] = useState(false);
  const [view, setView] = useAtom(transactionDetailsViewAtom);
  const showObservations = view === "observations";

  const {
    data,
    isFetching,
    isLoading,
    isSyncing,
    isSyncError,
    handleGoToSellerPage,
    handleGoToCustomerPage,
  } = useTransactionDetails();

  const { setCustomerData, setTransactionId, setProposalData } =
    useCrefazContext();

  const { isSupport, isSeller } = useAuth();
  const { pathname } = useLocation();
  // caso seja outro status vai aparecer como texto nos detalhes da transação
  const canEditSupport =
    isSupport &&
    !!data?.actualStatus &&
    [StatusEnum.PENDING, StatusEnum.ACTION_REQUIRED].includes(
      data.actualStatus,
    ) &&
    pathname.includes("/support");

  // só vendedor e suporte acessam, caso tenha documento ou permissqo para upload
  const hasDocButton = !!(
    (isSeller || isSupport) &&
    (data?.config?.canUploadDocs ||
      (data?.support?.documents?.length &&
        data?.support?.documents?.length > 0))
  );

  // chamado aberto com a proposta na tela já sai vinculado a ela
  useRegisterSupportContext(
    "transactionDetails",
    data
      ? {
          transactionId: data._id,
          proposal: data.contract ? String(data.contract) : undefined,
          product: data.type as PartnerType,
          step: PROPOSAL_DETAILS_STEP,
          data: {
            status: data.actualStatus ?? data.status,
            statusEsteira: data.statusEsteira,
            value: data.value,
            customerName: data.customer?.name,
            customerCpf: data.customer?.cpf,
            sellerName: data.seller?.name,
            teamName: data.team?.name,
            tableName: data.tableName,
          },
        }
      : null,
  );

  // volta para os detalhes ao trocar de transação e ao fechar a sheet
  useEffect(() => {
    setView("details");
    return () => setView("details");
  }, [data?._id, setView]);

  useEffect(() => {
    if (data) {
      setTransactionId(data._id);
      setCustomerData({
        cpf: data.customer.cpf,
        name: data.customer.name,
        birthDate: "",
      });
      setProposalData({ value: data.value, propostaId: String(data.contract) });
    }
  }, [data, setCustomerData, setProposalData, setTransactionId]);

  return (
    <div className="w-full">
      {(isFetching || isLoading) && (
        <div className="flex h-screen items-center justify-center">
          <Spinner />
        </div>
      )}
      {!isFetching && data && showObservations && (
        <ObservationsHistory observations={data.observacoes ?? []} />
      )}

      {!isFetching && data && !showObservations && (
        <>
          <div
            onClick={() =>
              isClient
                ? handleGoToCustomerPage(data.customer._id)
                : handleGoToSellerPage(data.seller._id)
            }
            className="flex cursor-pointer justify-between rounded-lg border-[1px] border-gray01 bg-[#F0F0F0] p-5"
          >
            <TransactionDetailsCard
              seller={isClient ? data.customer.name : data.seller.name}
              photo={isClient ? "" : data.seller.profileImageUrl}
              count={data.seller.transactions_count}
              isClient={isClient}
            />
            <div className="h-fit w-fit rounded-3xl bg-[#E6F0FB] px-2 py-1">
              <p className="text-sm font-semibold text-[#1960AB]">
                {isClient ? "Cliente" : "Vendedor"}
              </p>
            </div>
          </div>

          <TransactionsDetailsInfo
            data={data}
            isClient={isClient}
            isSyncing={isSyncing}
            isSyncError={isSyncError}
            onOpenObservations={() => setView("observations")}
          />

          {data.config?.canViewProof && (
            <Button
              variant="ghost"
              className="flex w-full items-center gap-2 border-[1px] border-[#DFDFE2]"
              onClick={() =>
                window.open(
                  `${import.meta.env.VITE_API_FGTS_URL}/transaction/${data._id}/proof`,
                )
              }
            >
              <List />
              <p className="text-xs font-semibold">Acessar comprovante</p>
            </Button>
          )}

          {canEditSupport && (
            <Button className="w-full text-white" onClick={onSupport}>
              Solicitar informações
            </Button>
          )}

          {hasDocButton && (
            <Button className="mt-4 w-full text-white" onClick={onDocuments}>
              Documentos
            </Button>
          )}

          {data.config?.canUploadFiles && <ModalUploadFiles />}

          {data.config?.canApprove && (
            <ModalApproveTransaction transactionId={data._id} />
          )}

          {data.config?.canRefuse && (
            <ModalRefuseTransaction transactionId={data._id} />
          )}

          {data.config?.canSimulate && (
            <>
              <Button
                type="button"
                className="mt-4 w-full text-white"
                onClick={() => setSimulatePanOpen(true)}
              >
                Realizar simulação
              </Button>
              <ModalSimulatePan
                open={simulatePanOpen}
                onOpenChange={setSimulatePanOpen}
                preTransactionId={data._id}
                customerId={data.customer._id}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default TransactionDetails;
